import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogContent,
  Chip,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import UserAvatar from "./UserAvatar";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import CloseIcon from "@mui/icons-material/Close";
import BlockIcon from "@mui/icons-material/Block";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import CheckIcon from "@mui/icons-material/Check";
import EmojiPicker from "emoji-picker-react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminMessages,
  fetchMoreAdminMessages,
  fetchPinnedMessages,
  fetchMorePinnedMessages,
  sendAdminMessage,
  appendMessage,
  clearUnreadForConversation,
  updateMessageStatus,
  updateConversationEnabled,
} from "../../features/messaging/messagingSlice";
import { getAdminUploadUrlApi, getAttachmentUrlApi, enableDMApi, disableDMApi, sendPinnedMessageApi } from "../../api/messagingApi";
import messagingBroadcast from "../../utils/messagingBroadcast";
import compressImage from "../../utils/compressImage";
import DOMPurify from "dompurify";

const linkify = (text) =>
  text
    .replace(/(https?:\/\/[^\s]+)/g, (url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:inherit;text-decoration:underline;font-weight:600;word-break:break-all;">${url}</a>`
    )
    .replace(/\n/g, "<br/>");

const formatTime = (date, tz) =>
  new Date(date).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    ...(tz ? { timeZone: tz } : {}),
  });

const formatDateLabel = (date, tz) => {
  const now = new Date();
  const d = new Date(date);
  const opts = tz ? { timeZone: tz } : {};
  if (d.toLocaleDateString([], opts) === now.toLocaleDateString([], opts)) return "Today";
  if (d.toLocaleDateString([], opts) === new Date(now - 86400000).toLocaleDateString([], opts)) return "Yesterday";
  if (now.getFullYear() === d.getFullYear()) {
    return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric", ...opts });
  }
  return d.toLocaleDateString([], { year: "numeric", month: "short", day: "numeric", ...opts });
};

// ─── Image Lightbox ───────────────────────────────────────────────────────────
const ImageLightbox = ({ src, open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="lg"
    PaperProps={{ sx: { bgcolor: "transparent", boxShadow: "none", overflow: "visible" } }}
    BackdropProps={{ sx: { bgcolor: "rgba(0,0,0,0.92)", backdropFilter: "blur(4px)" } }}
  >
    <DialogContent sx={{ p: 0, position: "relative", bgcolor: "transparent" }}>
      <Box sx={{ position: "fixed", top: 16, right: 16, display: "flex", gap: 1, zIndex: 10 }}>
        {/* <Tooltip title="Download">
          <IconButton
            onClick={() => { const a = document.createElement("a"); a.href = src; a.download = "attachment"; a.click(); }}
            sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff", backdropFilter: "blur(8px)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip> */}
        <IconButton onClick={onClose} sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff", backdropFilter: "blur(8px)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        component="img"
        src={src}
        alt="attachment"
        sx={{ maxWidth: "94vw", maxHeight: "92vh", display: "block", borderRadius: 2, objectFit: "contain", boxShadow: "0 24px 60px rgba(0,0,0,0.6)" }}
      />
    </DialogContent>
  </Dialog>
);

// ─── Date Divider ─────────────────────────────────────────────────────────────
const DateDivider = ({ label }) => (
  <Box display="flex" alignItems="center" gap={1.5} my={2.5} px={1}>
    <Box flex={1} height="1px" bgcolor="rgba(15,23,42,0.06)" />
    <Typography
      variant="caption"
      fontWeight={700}
      fontSize={10.5}
      color="#94A3B8"
      letterSpacing="1px"
      textTransform="uppercase"
      sx={{ bgcolor: "#F8FAFC", px: 1.5, py: 0.5, borderRadius: "10px", border: "1px solid rgba(15,23,42,0.05)" }}
    >
      {label}
    </Typography>
    <Box flex={1} height="1px" bgcolor="rgba(15,23,42,0.06)" />
  </Box>
);

// ─── Message Ticks ────────────────────────────────────────────────────────────
const MessageTicks = ({ msg, isAdmin, isPinnedConv }) => {
  if (!isAdmin) return null;
  // Pinned conv: coach admin IS the user, so their sent messages track admin-side read/delivered
  const read = isPinnedConv ? msg.readByAdmin : msg.readByUser;
  const delivered = isPinnedConv ? msg.deliveredToAdmin : msg.deliveredToUser;
  const color = read ? "#60A5FA" : "#94A3B8";
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", ml: 0.4, verticalAlign: "middle" }}>
      <Box component="span" sx={{ fontSize: "11px", lineHeight: 1, color, fontWeight: 700 }}>✓</Box>
      {(delivered || read) && (
        <Box component="span" sx={{ fontSize: "11px", lineHeight: 1, color, fontWeight: 700, ml: "-3px" }}>✓</Box>
      )}
    </Box>
  );
};

// ─── Message Bubble ───────────────────────────────────────────────────────────
const MessageBubble = ({ msg, isAdmin, isFirstInGroup, isLastInGroup, attachmentCache, onImageClick, timezone, isPinnedConv, userProfilePic, userName }) => {
  const time = formatTime(msg.createdAt, timezone);
  const imgSrc = msg.attachmentUrl ? attachmentCache[msg.attachmentUrl] : null;

  // Smooth grouping radii
  const radiusAdmin = `18px ${isFirstInGroup ? "18px" : "6px"} ${isLastInGroup ? "6px" : "6px"} 18px`;
  const radiusUser = `${isFirstInGroup ? "18px" : "6px"} 18px 18px ${isLastInGroup ? "6px" : "6px"}`;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-end",
        gap: 0.75,
        maxWidth: "70%",
        mb: isLastInGroup ? 1.25 : 0.25,
        alignSelf: isAdmin ? "flex-end" : "flex-start",
        animation: "msgIn 0.25s ease-out",
        "@keyframes msgIn": {
          from: { opacity: 0, transform: "translateY(6px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {/* User avatar — shown beside the last message in each group */}
      {!isAdmin && (
        <Box sx={{ width: 28, height: 28, flexShrink: 0, mb: isLastInGroup ? "18px" : 0 }}>
          {isLastInGroup && (
            <UserAvatar profilePic={userProfilePic} name={userName} size={28} fontSize={11} />
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            bgcolor: isAdmin ? "#368AD0" : "#FFFFFF",
            color: isAdmin ? "#fff" : "#0F172A",
            borderRadius: isAdmin ? radiusAdmin : radiusUser,
            boxShadow: isAdmin
              ? "0 4px 14px rgba(54,138,208,0.25), inset 0 1px 0 rgba(255,255,255,0.15)"
              : "0 2px 8px rgba(15,23,42,0.06), 0 0 0 1px rgba(15,23,42,0.04)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {msg.attachmentType === "image" && (
            <Box
              sx={{
                position: "relative",
                cursor: imgSrc ? "pointer" : "default",
                display: "block",
                width: "fit-content",
              }}
              onClick={() => imgSrc && onImageClick(imgSrc)}
            >
              {imgSrc ? (
                <>
                  <Box
                    component="img"
                    src={imgSrc}
                    alt="attachment"
                    sx={{
                      maxWidth: 280,
                      maxHeight: 220,
                      display: "block",
                      objectFit: "cover",
                      borderRadius: msg.content ? "inherit inherit 0 0" : "inherit",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute", inset: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "background 0.2s",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.35)", "& .zoom-icon": { opacity: 1, transform: "scale(1)" } },
                    }}
                  >
                    <ZoomInIcon
                      className="zoom-icon"
                      sx={{
                        opacity: 0,
                        color: "#fff",
                        fontSize: 36,
                        transform: "scale(0.7)",
                        transition: "all 0.2s",
                        filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.5))",
                      }}
                    />
                  </Box>
                </>
              ) : (
                <Box sx={{ width: 220, height: 160, bgcolor: "rgba(15,23,42,0.04)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CircularProgress size={20} sx={{ color: isAdmin ? "#fff" : "#94A3B8" }} />
                </Box>
              )}
            </Box>
          )}
          {msg.content && (
            <Box sx={{ px: 1.75, pt: 1.25, pb: 1 }}>
              <Typography
                variant="body2"
                sx={{ wordBreak: "break-word", lineHeight: 1.5, fontSize: 13.5, fontWeight: 500 }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(linkify(msg.content), { ADD_ATTR: ["target", "rel"] }),
                }}
              />
            </Box>
          )}
        </Box>

        {isLastInGroup && (
          <Box display="flex" alignItems="center" gap={0.25} mt={0.4} px={0.5} alignSelf={isAdmin ? "flex-end" : "flex-start"}>
            <Typography variant="caption" sx={{ fontSize: "0.66rem", color: "#94A3B8", fontWeight: 500 }}>
              {time}
            </Typography>
            <MessageTicks msg={msg} isAdmin={isAdmin} isPinnedConv={isPinnedConv} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Typing Indicator ─────────────────────────────────────────────────────────
const TypingDots = () => (
  <Box sx={{ display: "flex", gap: 0.6, alignItems: "center", px: 2.25, py: 1.5, bgcolor: "#FFFFFF", borderRadius: "18px 18px 18px 6px", boxShadow: "0 2px 8px rgba(15,23,42,0.06), 0 0 0 1px rgba(15,23,42,0.04)", alignSelf: "flex-start", mb: 1.25 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 7, height: 7, borderRadius: "50%", bgcolor: "#94A3B8",
          animation: `typingBounce 1.3s infinite ease-in-out ${i * 0.15}s`,
          "@keyframes typingBounce": {
            "0%, 60%, 100%": { transform: "translateY(0)", opacity: 0.4 },
            "30%": { transform: "translateY(-5px)", opacity: 1 },
          },
        }}
      />
    ))}
  </Box>
);

// ─── Attachment Preview Bar ───────────────────────────────────────────────────
const AttachmentPreview = ({ file, previewUrl, onRemove }) => (
  <Box
    sx={{
      px: 2, py: 1.25,
      bgcolor: "#EFF6FF",
      borderTop: "1px solid rgba(54,138,208,0.15)",
      display: "flex", alignItems: "center", gap: 1.5,
    }}
  >
    <Box sx={{ position: "relative" }}>
      <Box
        component="img"
        src={previewUrl}
        alt="preview"
        sx={{ width: 56, height: 56, objectFit: "cover", borderRadius: "10px", border: "2px solid #fff", boxShadow: "0 2px 8px rgba(54,138,208,0.25)" }}
      />
      <Box sx={{ position: "absolute", top: -4, right: -4, bgcolor: "#10B981", color: "#fff", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff" }}>
        <CheckIcon sx={{ fontSize: 11 }} />
      </Box>
    </Box>
    <Box flex={1} overflow="hidden">
      <Typography fontSize={13} fontWeight={700} noWrap color="#0F172A">{file.name}</Typography>
      <Typography variant="caption" color="#64748B" fontSize={11}>
        {(file.size / 1024).toFixed(0)} KB · Ready to send
      </Typography>
    </Box>
    <IconButton size="small" onClick={onRemove} sx={{ color: "#DC2626", "&:hover": { bgcolor: "#FEE2E2" } }}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

// ─── Group messages by date + sender for cleaner UI ───────────────────────────
const groupMessages = (messages, tz) => {
  const groups = [];
  let lastDate = null;
  let prevSenderType = null;
  let prevTimestamp = null;
  const opts = tz ? { timeZone: tz } : {};

  messages.forEach((msg, idx) => {
    const d = new Date(msg.createdAt);
    const dateKey = d.toLocaleDateString([], opts);
    if (dateKey !== lastDate) {
      groups.push({ type: "date", label: formatDateLabel(d, tz), key: `date-${dateKey}` });
      lastDate = dateKey;
      prevSenderType = null;
      prevTimestamp = null;
    }
    const sameSender = prevSenderType === msg.senderType;
    const closeInTime = prevTimestamp && d - prevTimestamp < 5 * 60 * 1000;
    const isFirstInGroup = !sameSender || !closeInTime;
    const next = messages[idx + 1];
    let isLastInGroup = true;
    if (next) {
      const nd = new Date(next.createdAt);
      const sameNext = next.senderType === msg.senderType;
      const closeNext = nd - d < 5 * 60 * 1000 && nd.toLocaleDateString([], opts) === dateKey;
      if (sameNext && closeNext) isLastInGroup = false;
    }
    groups.push({ type: "msg", msg, isFirstInGroup, isLastInGroup, key: msg._id });
    prevSenderType = msg.senderType;
    prevTimestamp = d;
  });
  return groups;
};

// ─── ChatPanel ────────────────────────────────────────────────────────────────
const ChatPanel = ({ conversation, adminId, socket, onClose }) => {
  const isPinned = !!conversation.isPinnedSuperAdmin;
  const dispatch = useDispatch();
  const messages = useSelector((state) => state.messaging.messages[conversation._id] || []);
  const hasMore = useSelector((state) => state.messaging.hasMore[conversation._id] ?? false);
  const loadingMore = useSelector((state) => state.messaging.loadingMore[conversation._id] ?? false);
  const sending = useSelector((state) => state.messaging.sendingMessage);

  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [attachmentCache, setAttachmentCache] = useState({});
  const [lightbox, setLightbox] = useState({ open: false, src: "" });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [userOnline, setUserOnline] = useState(false);

  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const textInputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const initialScrollDoneRef = useRef(false);
  const pageRef = useRef(1);
  const prevScrollHeightRef = useRef(null);
  const userScrolledRef = useRef(false);

  const baseUserName = conversation.userDisplayName || conversation.userFullName || conversation.userName || conversation.userEmail;
  const userName = isPinned
    ? (conversation.adminName || "Three Pillars Method")
    : baseUserName;

  // Use the admin's own browser timezone so timestamps reflect the admin's local time
  const adminTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const grouped = useMemo(() => groupMessages(messages, adminTz), [messages, adminTz]);

  useEffect(() => {
    if (isPinned) {
      dispatch(fetchPinnedMessages({ conversationId: conversation._id }));
    } else {
      dispatch(fetchAdminMessages({ conversationId: conversation._id }));
    }
    dispatch(clearUnreadForConversation(conversation._id));
    if (socket?.connected) {
      socket.emit("mark_read", { conversationId: conversation._id });
      messagingBroadcast.postMessage({ type: "conv_read", conversationId: conversation._id });
    }
    setTimeout(() => textInputRef.current?.focus(), 300);
  }, [dispatch, conversation._id]);

  useEffect(() => {
    if (!socket) return;
    socket.emit("join_conversation", { conversationId: conversation._id });
    // Also emit mark_read on join (socket may not have been ready in the effect above)
    socket.emit("mark_read", { conversationId: conversation._id });
    messagingBroadcast.postMessage({ type: "conv_read", conversationId: conversation._id });

    const handler = (msg) => {
      if (msg.conversationId === conversation._id) {
        dispatch(appendMessage(msg));
        dispatch(clearUnreadForConversation(conversation._id));
        // Mark read for the "incoming" side of each conversation type
        const incomingSenderType = isPinned ? "admin" : "user";
        if (msg.senderType === incomingSenderType) {
          socket.emit("mark_read", { conversationId: conversation._id });
          // Tell other tabs this message was read here (only if this tab is visible)
          if (!document.hidden) {
            messagingBroadcast.postMessage({ type: "conv_read", conversationId: conversation._id });
          }
        }
      }
    };
    const statusHandler = ({ userId, userType, isOnline }) => {
      if (userType === "user" && userId === conversation.userId?.toString()) {
        setUserOnline(isOnline);
      }
    };
    const msgStatusHandler = ({ conversationId, messageIds, status }) => {
      if (conversationId === conversation._id) {
        dispatch(updateMessageStatus({ conversationId, messageIds, status }));
      }
    };
    socket.on("new_message", handler);
    socket.on("participant_status", statusHandler);
    socket.on("message_status_update", msgStatusHandler);
    return () => {
      socket.off("new_message", handler);
      socket.off("participant_status", statusHandler);
      socket.off("message_status_update", msgStatusHandler);
      socket.emit("leave_conversation", { conversationId: conversation._id });
    };
  }, [socket, conversation._id, dispatch]);

  // Re-emit mark_read when admin tabs back to the window with this conversation open
  useEffect(() => {
    const onFocus = () => {
      if (socket?.connected) {
        socket.emit("mark_read", { conversationId: conversation._id });
      }
    };
    const onVisibility = () => {
      if (!document.hidden) onFocus();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [socket, conversation._id]);

  useEffect(() => {
    const load = async () => {
      const keys = messages.filter((m) => m.attachmentUrl && !attachmentCache[m.attachmentUrl]).map((m) => m.attachmentUrl);
      for (const key of keys) {
        try { const d = await getAttachmentUrlApi(key); setAttachmentCache((p) => ({ ...p, [key]: d.signedUrl })); } catch {}
      }
    };
    if (messages.length) load();
  }, [messages]);

  // Reset scroll state when switching conversations
  useEffect(() => {
    initialScrollDoneRef.current = false;
    pageRef.current = 1;
    prevScrollHeightRef.current = null;
    userScrolledRef.current = false;
  }, [conversation._id]);

  // Mark genuine user-initiated scroll so pagination never fires from programmatic jumps
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const mark = () => { userScrolledRef.current = true; };
    container.addEventListener('wheel', mark, { passive: true });
    container.addEventListener('touchstart', mark, { passive: true });
    return () => {
      container.removeEventListener('wheel', mark);
      container.removeEventListener('touchstart', mark);
    };
  }, [conversation._id]);

  // Scroll to bottom — skipped when a pagination load is in progress so the
  // restoration effect below can anchor the view to the previous position.
  useEffect(() => {
    if (!messages.length) return;
    if (prevScrollHeightRef.current !== null) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const jumpToBottom = (smooth = false) => {
      if (smooth) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      else container.scrollTop = container.scrollHeight;
    };
    if (!initialScrollDoneRef.current) {
      jumpToBottom(false);
      requestAnimationFrame(() => jumpToBottom(false));
      const timers = [50, 200, 500, 1000].map((ms) => setTimeout(() => jumpToBottom(false), ms));
      initialScrollDoneRef.current = true;
      return () => timers.forEach(clearTimeout);
    } else {
      jumpToBottom(true);
    }
  }, [messages]);

  // After older messages are prepended, restore scroll position.
  // Reset userScrolledRef BEFORE setting scrollTop so the resulting onScroll
  // event cannot cascade into another pagination call.
  useEffect(() => {
    if (prevScrollHeightRef.current !== null) {
      const container = scrollContainerRef.current;
      userScrolledRef.current = false;
      if (container) {
        container.scrollTop = container.scrollHeight - prevScrollHeightRef.current;
      }
      prevScrollHeightRef.current = null;
    }
  }, [messages]);

  // Re-anchor when an image finishes loading — only if already near the bottom.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const imgs = container.querySelectorAll("img");
    if (!imgs.length) return;
    const onLoad = () => {
      const distFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distFromBottom < 200) container.scrollTop = container.scrollHeight;
    };
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", onLoad, { once: true });
    });
    return () => imgs.forEach((img) => img.removeEventListener("load", onLoad));
  }, [messages, attachmentCache]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setErrorMsg("Please select an image file");
      e.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrorMsg("Image must be under 10MB");
      e.target.value = "";
      return;
    }
    setPendingFile(file);
    setPendingPreview(URL.createObjectURL(file));
    e.target.value = "";
  };

  const clearPending = () => {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(null);
    setPendingPreview(null);
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed && !pendingFile) return;
    setShowEmoji(false);

    if (pendingFile) {
      setUploading(true);
      try {
        const compressed = await compressImage(pendingFile);
        const contentType = "image/jpeg";
        const safeName = compressed.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const { signedUrl, key } = await getAdminUploadUrlApi(safeName, contentType);
        if (!signedUrl || !key) throw new Error("Failed to get upload URL");
        const putRes = await fetch(signedUrl, { method: "PUT", body: compressed, headers: { "Content-Type": contentType } });
        if (!putRes.ok) {
          const errText = await putRes.text().catch(() => "");
          throw new Error(`Upload failed (${putRes.status}): ${errText.slice(0, 120) || putRes.statusText}`);
        }
        clearPending();
        if (isPinned) {
          const data = await sendPinnedMessageApi({ content: trimmed, attachmentUrl: key, attachmentType: "image" });
          dispatch(appendMessage(data.message));
        } else {
          await dispatch(sendAdminMessage({ conversationId: conversation._id, content: trimmed, attachmentUrl: key, attachmentType: "image" }));
        }
      } catch (err) {
        console.error("Upload error:", err);
        setErrorMsg(err.message || "Failed to send image");
      } finally {
        setUploading(false);
      }
    } else {
      setText("");
      if (isPinned) {
        try {
          const data = await sendPinnedMessageApi({ content: trimmed });
          dispatch(appendMessage(data.message));
        } catch (err) {
          console.error("Send pinned error:", err);
          setErrorMsg(err.message || "Failed to send message");
        }
      } else {
        await dispatch(sendAdminMessage({ conversationId: conversation._id, content: trimmed }));
      }
    }
    setText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const isDmEnabled = conversation.isEnabled !== false;
  const [togglingEnable, setTogglingEnable] = useState(false);

  const handleToggleDm = async () => {
    if (togglingEnable) return;
    setTogglingEnable(true);
    const next = !isDmEnabled;
    dispatch(updateConversationEnabled({ conversationId: conversation._id, isEnabled: next }));
    try {
      if (next) await enableDMApi(conversation.userId);
      else await disableDMApi(conversation.userId);
    } catch (err) {
      dispatch(updateConversationEnabled({ conversationId: conversation._id, isEnabled: !next }));
      console.error("Toggle DM error:", err);
    } finally {
      setTogglingEnable(false);
    }
  };

  const canSend = isDmEnabled && !sending && !uploading && (!!text.trim() || !!pendingFile);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#FFFFFF", overflow: "hidden", position: "relative" }}>
      {/* Header */}
      <Box
        sx={{
          px: 3, py: 2,
          bgcolor: "#FFFFFF",
          borderBottom: "1px solid rgba(15,23,42,0.06)",
          display: "flex", alignItems: "center", gap: 2,
          flexShrink: 0,
          backdropFilter: "blur(20px)",
        }}
      >
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          {isPinned ? (
            <Box sx={{ width: 46, height: 46, borderRadius: "50%", bgcolor: "#14142B", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #368AD0" }}>
              <Box component="img" src="/images/threepillars.png" alt="Three Pillars Method" sx={{ width: 28, height: 28, objectFit: "contain" }} />
            </Box>
          ) : (
            <UserAvatar profilePic={conversation.userProfilePic} name={userName} size={46} fontSize={16} />
          )}
          {!isPinned && userOnline && (
            <Box
              sx={{
                position: "absolute",
                bottom: 1,
                right: 1,
                width: 11,
                height: 11,
                borderRadius: "50%",
                bgcolor: "#10B981",
                border: "2.5px solid #fff",
              }}
            />
          )}
        </Box>
        <Box flex={1} overflow="hidden">
          <Typography fontWeight={800} fontSize={16} color="#0F172A" noWrap letterSpacing="-0.3px">
            {userName}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="caption" color={"#64748B"} fontSize={12} noWrap display="block" fontWeight={userOnline ? 600 : 400}>
              {isPinned ? "Three Pillars Method" : conversation.userEmail}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={0.5}>
          <Tooltip title="More">
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ color: "#64748B", "&:hover": { bgcolor: "#F1F5F9", color: "#368AD0" } }}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {onClose && (
            <Tooltip title="Close">
              <IconButton size="small" onClick={onClose} sx={{ color: "#64748B", "&:hover": { bgcolor: "#FEE2E2", color: "#DC2626" } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(null)}
          PaperProps={{
            sx: {
              mt: 1, borderRadius: 2,
              boxShadow: "0 10px 40px rgba(15,23,42,0.12)",
              border: "1px solid rgba(15,23,42,0.06)",
              minWidth: 180,
            },
          }}
        >
          <MenuItem onClick={() => { dispatch(fetchAdminMessages({ conversationId: conversation._id })); setMenuAnchor(null); }} sx={{ fontSize: 13 }}>
            Reload messages
          </MenuItem>
          <MenuItem
            onClick={() => { setMenuAnchor(null); handleToggleDm(); }}
            sx={{ fontSize: 13, color: isDmEnabled ? "#DC2626" : "#368AD0", fontWeight: 600 }}
          >
            {isDmEnabled ? "Disable DM" : "Re-enable DM"}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              window.dispatchEvent(
                new CustomEvent("admin:viewUserProfile", {
                  detail: { email: conversation.userEmail, userId: conversation.userId },
                })
              );
            }}
            sx={{ fontSize: 13 }}
          >
            View user profile
          </MenuItem>
        </Menu>
      </Box>

      {/* Disabled DM banner */}
      {!isDmEnabled && (
        <Box
          sx={{
            mx: 3,
            mt: 1.5,
            mb: 1,
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: "#FEF3C7",
            border: "1px solid #FDE68A",
            borderRadius: "12px",
          }}
        >
          <Box
            sx={{
              width: 32, height: 32, borderRadius: "50%",
              bgcolor: "#F59E0B", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <BlockIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box flex={1} overflow="hidden">
            <Typography fontSize={13} fontWeight={700} color="#78350F">
              Direct messaging is disabled
            </Typography>
            <Typography variant="caption" color="#92400E" fontSize={11.5}>
              The user can't see this chat. Re-enable to resume messaging.
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleToggleDm}
            disabled={togglingEnable}
            sx={{
              bgcolor: "#F59E0B",
              color: "#fff",
              borderRadius: "8px",
              px: 1.5,
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
              "&:hover": { bgcolor: "#D97706" },
              "&.Mui-disabled": { bgcolor: "#FCD34D", color: "#fff" },
            }}
          >
            {togglingEnable ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              <Typography component="span" fontSize={12} fontWeight={700}>
                Re-enable
              </Typography>
            )}
          </IconButton>
        </Box>
      )}

      {/* Messages Area */}
      <Box
        ref={scrollContainerRef}
        onScroll={(e) => {
          const el = e.target;
          if (!userScrolledRef.current) return;
          if (el.scrollTop < 60 && hasMore && !loadingMore && messages.length > 0) {
            prevScrollHeightRef.current = el.scrollHeight;
            const nextPage = pageRef.current + 1;
            pageRef.current = nextPage;
            if (isPinned) {
              dispatch(fetchMorePinnedMessages({ conversationId: conversation._id, page: nextPage }));
            } else {
              dispatch(fetchMoreAdminMessages({ conversationId: conversation._id, page: nextPage }));
            }
          }
        }}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 3,
          py: 2.5,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#FAFBFF",
          "&::-webkit-scrollbar": { width: 7 },
          "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
          "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(148,163,184,0.3)", borderRadius: 4 },
          "&::-webkit-scrollbar-thumb:hover": { bgcolor: "rgba(148,163,184,0.5)" },
        }}
      >
        {/* Top loading spinner when fetching older pages */}
        {loadingMore && (
          <Box display="flex" justifyContent="center" alignItems="center" py={1.5} flexShrink={0}>
            <CircularProgress size={18} sx={{ color: "#368AD0" }} />
            <Typography variant="caption" color="#64748B" ml={1} fontSize={12}>
              Loading older messages…
            </Typography>
          </Box>
        )}

        {/* Beginning of conversation indicator */}
        {!hasMore && messages.length > 0 && !loadingMore && (
          <Box display="flex" justifyContent="center" py={1} flexShrink={0}>
            <Typography variant="caption" color="#94A3B8" fontSize={11}>
              Beginning of conversation
            </Typography>
          </Box>
        )}

        {messages.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={1.5}>
            <Box
              sx={{
                width: 88, height: 88, borderRadius: "50%",
                bgcolor: "#EFF6FF",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 40,
                boxShadow: "0 6px 18px rgba(54,138,208,0.12)",
              }}
            >
              👋
            </Box>
            <Typography fontWeight={800} fontSize={17} color="#0F172A" letterSpacing="-0.3px">
              Say hi to {userName}
            </Typography>
            <Typography variant="body2" color="#64748B" textAlign="center" maxWidth={300} fontSize={13}>
              Send your first message to kick off the conversation. They'll receive a notification right away.
            </Typography>
          </Box>
        ) : (
          grouped.map((g) => {
            if (g.type === "date") return <DateDivider key={g.key} label={g.label} />;
            return (
              <MessageBubble
                key={g.key}
                msg={g.msg}
                isAdmin={isPinned ? g.msg.senderType === "user" : g.msg.senderType === "admin"}
                isFirstInGroup={g.isFirstInGroup}
                isLastInGroup={g.isLastInGroup}
                attachmentCache={attachmentCache}
                onImageClick={(src) => setLightbox({ open: true, src })}
                timezone={adminTz}
                isPinnedConv={isPinned}
                userProfilePic={conversation.userProfilePic}
                userName={userName}
              />
            );
          })
        )}
        <div ref={bottomRef} />
      </Box>

      {/* Attachment Preview */}
      {pendingFile && pendingPreview && (
        <AttachmentPreview file={pendingFile} previewUrl={pendingPreview} onRemove={clearPending} />
      )}

      {/* Emoji Picker */}
      {showEmoji && (
        <Box sx={{ position: "absolute", bottom: pendingFile ? 165 : 84, right: 16, zIndex: 30, boxShadow: "0 12px 40px rgba(15,23,42,0.18)", borderRadius: "12px", overflow: "hidden" }}>
          <EmojiPicker
            onEmojiClick={(e) => setText((p) => p + e.emoji)}
            height={360}
            width={320}
            searchDisabled={false}
            skinTonesDisabled
          />
        </Box>
      )}

      {/* Composer */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderTop: "1px solid rgba(15,23,42,0.06)",
          bgcolor: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          minHeight: 72,
        }}
      >
        <Tooltip title={isDmEnabled ? "Attach image" : "Re-enable DM to send"}>
          <span>
            <IconButton
              size="medium"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !isDmEnabled}
              sx={{
                color: pendingFile ? "#368AD0" : "#64748B",
                bgcolor: pendingFile ? "#EFF6FF" : "transparent",
                width: 40,
                height: 40,
                "&:hover": { bgcolor: "#F1F5F9", color: "#368AD0" },
                "&.Mui-disabled": { color: "#CBD5E1" },
              }}
            >
              <ImageOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={isDmEnabled ? "Emoji" : "Re-enable DM to send"}>
          <span>
          <IconButton
            size="medium"
            onClick={() => setShowEmoji((s) => !s)}
            disabled={!isDmEnabled}
            sx={{
              color: showEmoji ? "#368AD0" : "#64748B",
              bgcolor: showEmoji ? "#EFF6FF" : "transparent",
              width: 40,
              height: 40,
              "&:hover": { bgcolor: "#F1F5F9", color: "#368AD0" },
              "&.Mui-disabled": { color: "#CBD5E1" },
            }}
          >
            <EmojiEmotionsOutlinedIcon fontSize="small" />
          </IconButton>
          </span>
        </Tooltip>

        <input type="file" ref={fileInputRef} accept="image/*" style={{ display: "none" }} onChange={handleFileSelect} />

        <TextField
          multiline
          maxRows={5}
          fullWidth
          inputRef={textInputRef}
          disabled={!isDmEnabled}
          placeholder={
            isDmEnabled
              ? `Message ${(userName || "").split(" ")[0] || "user"}...`
              : "DM is disabled — re-enable to send messages"
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "24px",
              fontSize: 14,
              fontWeight: 500,
              bgcolor: "#F1F5F9",
              transition: "all 0.15s",
              padding: "10px 18px",
              minHeight: 44,
              "& fieldset": { border: "none" },
              "&:hover": { bgcolor: "#E2E8F0" },
              "&.Mui-focused": { bgcolor: "#FFFFFF", boxShadow: "0 0 0 2px rgba(54,138,208,0.25)" },
            },
            "& .MuiOutlinedInput-input": { padding: 0 },
          }}
        />

        <Tooltip title="Send (Enter)">
          <span>
            <IconButton
              onClick={handleSend}
              disabled={!canSend}
              sx={{
                bgcolor: canSend ? "#368AD0" : "#E2E8F0",
                color: canSend ? "#fff" : "#94A3B8",
                width: 44,
                height: 44,
                flexShrink: 0,
                transition: "all 0.18s",
                boxShadow: canSend ? "0 4px 12px rgba(54,138,208,0.3)" : "none",
                "&:hover": {
                  bgcolor: canSend ? "#1F5D8A" : "#E2E8F0",
                  transform: canSend ? "scale(1.05)" : "none",
                },
                "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
              }}
            >
              {(sending || uploading) ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                <SendIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <ImageLightbox open={lightbox.open} src={lightbox.src} onClose={() => setLightbox({ open: false, src: "" })} />

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={5000}
        onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")} sx={{ fontSize: 13 }}>{errorMsg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatPanel;
